import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { ProposalStage } from "generated/graphql";
import useUpdateProposalStagesOrdering from "hooks/proposals/useUpdateProposalStagesOrdering";
import { useCallback, useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { draftColors } from "./draftDesignTokens";

type ReorderStagesDrawerProps = {
  open: boolean;
  onClose: () => void;
  stages: ProposalStage[];
  proposalId: string;
};

export default function ReorderStagesDrawer({
  open,
  onClose,
  stages,
  proposalId,
}: ReorderStagesDrawerProps) {
  const { sendRequest } = useUpdateProposalStagesOrdering();
  const [sorted, setSorted] = useState<ProposalStage[]>([]);

  useEffect(() => {
    setSorted([...stages].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)));
  }, [stages]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const items = Array.from(sorted);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      setSorted(items);
    },
    [sorted]
  );

  const handleSave = useCallback(() => {
    sendRequest({
      id: proposalId,
      stages: {
        update: sorted.map((item, index) => ({ id: item.id, ordering: index + 1 })),
      },
    });
    onClose();
  }, [sorted, proposalId, sendRequest, onClose]);

  return (
    <UiSideModal open={open} onClose={onClose} title="Reorder Jobs" width="380px">
      <MDBox p="16px">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="stages-list">
            {(provided) => (
              <MDBox ref={provided.innerRef} {...provided.droppableProps}>
                {sorted.map((stage, index) => (
                  <Draggable key={stage.id} draggableId={stage.id} index={index}>
                    {(provided, snapshot) => (
                      <MDBox
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        display="flex"
                        alignItems="center"
                        gap="8px"
                        px="12px"
                        py="10px"
                        mb="6px"
                        sx={{
                          borderRadius: "8px",
                          border: `1px solid ${draftColors.border}`,
                          backgroundColor: snapshot.isDragging ? "#f0f4ff" : "#fff",
                          cursor: "grab",
                          "&:active": { cursor: "grabbing" },
                        }}
                      >
                        <Icon
                          sx={{ color: draftColors.textSecondary, fontSize: "18px !important" }}
                        >
                          drag_indicator
                        </Icon>
                        <UiTypography uiVariant="body-sm" sx={{ fontWeight: 500 }}>
                          {stage.name || "Unnamed Job"}
                        </UiTypography>
                      </MDBox>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </MDBox>
            )}
          </Droppable>
        </DragDropContext>

        <MDBox mt="12px">
          <UiButton uiVariant="primary-green" fullWidth onClick={handleSave}>
            Save
          </UiButton>
        </MDBox>
      </MDBox>
    </UiSideModal>
  );
}
